import React, { useState, useEffect } from 'react';
import { 
  IconButton, 
  Tooltip, 
  CircularProgress,
  Box,
  Typography
} from '@mui/material';
import { 
  SmartToy as BotIcon,
  SmartToyOutlined as BotOffIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../config/environment';

interface BotToggleButtonProps {
  conversationId: string | null;
  onStatusChange?: (enabled: boolean) => void;
  size?: 'small' | 'medium' | 'large';
  showLabel?: boolean;
}

export const BotToggleButton: React.FC<BotToggleButtonProps> = ({
  conversationId,
  onStatusChange,
  size = 'medium',
  showLabel = false
}) => {
  const [botEnabled, setBotEnabled] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Carregar status inicial do bot
  useEffect(() => {
    const fetchBotStatus = async () => {
      try {
        setLoading(true);
        
        // Verificar se é uma conversa de instância (não existe no banco)
        if (conversationId && conversationId.startsWith('instance-')) {
          // Para conversas de instância, usar status padrão (bot habilitado)
          setBotEnabled(true);
          setLoading(false);
          return;
        }

        const response = await axios.get(`${getApiUrl()}/conversations/${conversationId}/bot-status`);
        
        if (response.data.success) {
          setBotEnabled(response.data.data.bot_enabled);
        }
      } catch (err: any) {
        console.error('Error fetching bot status:', err);
        setError('Erro ao carregar status do bot');
      } finally {
        setLoading(false);
      }
    };

    if (conversationId) {
      fetchBotStatus();
    }
  }, [conversationId]);

  // Função para alternar o status do bot
  const toggleBot = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const newStatus = !botEnabled;
      
      // Verificar se é uma conversa de instância (não existe no banco)
      if (conversationId && conversationId.startsWith('instance-')) {
        // Para conversas de instância, apenas atualizar o estado local
        setBotEnabled(newStatus);
        onStatusChange?.(newStatus);
        setLoading(false);
        return;
      }
      
      const response = await axios.post(`${getApiUrl()}/conversations/${conversationId}/toggle-bot`, {
        enabled: newStatus
      });
      
      if (response.data.success) {
        setBotEnabled(newStatus);
        onStatusChange?.(newStatus);
      } else {
        setError('Erro ao alterar status do bot');
      }
    } catch (err: any) {
      console.error('Error toggling bot:', err);
      setError('Erro ao alterar status do bot');
    } finally {
      setLoading(false);
    }
  };

  // Determinar ícone baseado no status
  const getIcon = () => {
    if (loading) {
      return <CircularProgress size={20} />;
    }
    
    if (error) {
      return <WarningIcon color="error" />;
    }
    
    return botEnabled ? <BotIcon /> : <BotOffIcon />;
  };

  // Determinar cor baseada no status
  const getColor = () => {
    if (loading || error) return 'default';
    return botEnabled ? 'primary' : 'default';
  };

  // Determinar tooltip baseado no status
  const getTooltip = () => {
    if (loading) return 'Carregando...';
    if (error) return 'Erro ao carregar status do bot';
    return botEnabled 
      ? 'Bot ativo - Clique para desativar (suporte humano)' 
      : 'Bot desativado - Clique para ativar (resposta automática)';
  };

  // Determinar label baseado no status
  const getLabel = () => {
    if (loading) return 'Carregando...';
    if (error) return 'Erro';
    return botEnabled ? 'Bot Ativo' : 'Bot Desativado';
  };

  // Não renderizar se não há conversa selecionada
  if (!conversationId) {
    return null;
  }

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Tooltip title={getTooltip()} arrow>
        <IconButton
          onClick={toggleBot}
          disabled={loading}
          color={getColor()}
          size={size}
          sx={{
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'scale(1.1)',
              backgroundColor: botEnabled 
                ? 'rgba(25, 118, 210, 0.1)' 
                : 'rgba(158, 158, 158, 0.1)'
            }
          }}
        >
          {getIcon()}
        </IconButton>
      </Tooltip>
      
      {showLabel && (
        <Typography 
          variant="caption" 
          color={error ? 'error' : botEnabled ? 'primary' : 'text.secondary'}
          sx={{ 
            fontWeight: botEnabled ? 600 : 400,
            fontSize: '0.75rem'
          }}
        >
          {getLabel()}
        </Typography>
      )}
    </Box>
  );
};

export default BotToggleButton; 